import { useCallback, useEffect, useState } from 'react';
import { ConfigurableParameterDto, GuaranteeDto } from 'api/pricing';
import { useAppDispatch } from 'app/hooks';
import { usePanel } from 'hooks/usePanel';
import { ContractFunnel } from '../types/interfaces';
import { updateContractFunnelGuarantees } from '../slices/pricing/pricingSlice';

interface UseContractGuaranteesProps {
  contractFunnel: ContractFunnel;
}

export interface GuaranteesValuesWarning {
  value: number,
  parameters?: ConfigurableParameterDto
  warningMessage?: string,
}

interface GuaranteeValues {
  deductible?: GuaranteesValuesWarning;
  ceiling?: GuaranteesValuesWarning;
}

export const useContractGuarantees = ({ contractFunnel }: UseContractGuaranteesProps) => {
  const [activeGuarantees, setActiveGuarantees] = useState<GuaranteeDto[]>(contractFunnel.guarantees);
  const [guaranteeValues, setGuaranteeValues] = useState<Record<string, GuaranteeValues>>({});

  const { setPanelActions, closePanel } = usePanel();
  const dispatch = useAppDispatch();

  // Initialiser les valeurs des garanties
  useEffect(() => {
    const initialValues: Record<string, GuaranteeValues> = {};
    contractFunnel?.guarantees.forEach(guarantee => {
      const guaranteeValue: GuaranteeValues = {};
      if (guarantee.deductible?.value) {
        guaranteeValue.deductible = {
          value: guarantee.deductible.value,
          parameters: guarantee.deductible.parameters,
          warningMessage: guarantee.deductible.warningMessage ? guarantee.deductible.warningMessage : undefined,
        }
      }
      if (guarantee.ceiling?.value) {
        guaranteeValue.ceiling = {
          value: guarantee.ceiling.value,
          parameters: guarantee.ceiling.parameters,
          warningMessage: guarantee.ceiling.warningMessage ? guarantee.ceiling.warningMessage : undefined,
        }
      }
      if (guaranteeValue.deductible || guaranteeValue.ceiling) {
        initialValues[guarantee.id] = guaranteeValue;
      }
    });
    setGuaranteeValues(initialValues);
  }, [contractFunnel]);

  const updateDeductible = useCallback((guaranteeId: string, value: number, warningMessage?: string) => {
    setGuaranteeValues(prev => ({
      ...prev,
      [guaranteeId]: {
        ...prev[guaranteeId],
        deductible: {
          ...prev[guaranteeId]?.deductible,
          value,
          warningMessage
        },
      }
    }));
  }, []);

  const updateCeiling = useCallback((guaranteeId: string, value: number, warningMessage?: string) => {
    setGuaranteeValues(prev => ({
      ...prev,
      [guaranteeId]: {
        ...prev[guaranteeId],
        ceiling: {
          ...prev[guaranteeId]?.ceiling,
          value,
          warningMessage
        }
      }
    }));
  }, []);

  const getGuaranteeValue = useCallback((guaranteeId: string): GuaranteeValues => {
    return guaranteeValues[guaranteeId] || {};
  }, [guaranteeValues]);

  const isActive = useCallback((guaranteeId: string): boolean => {
    return activeGuarantees.some(g => g.id === guaranteeId);
  }, [activeGuarantees]);

  const toggleGuarantee = useCallback((guarantee: GuaranteeDto) => {
    setActiveGuarantees(prev => {
      const isCurrentlyActive = prev.some(g => g.id === guarantee.id);
      if (isCurrentlyActive) {
        return prev.filter(g => g.id !== guarantee.id);
      } else {
        return [...prev, guarantee];
      }
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const newGuarantees = activeGuarantees.map((guarantee) => {
      const currentGuaranteeValues = getGuaranteeValue(guarantee.id);

      return {
        ...guarantee,
        ceiling: guarantee.ceiling ? {
          ...guarantee.ceiling,
          value: currentGuaranteeValues.ceiling
            ? currentGuaranteeValues.ceiling.value
            : guarantee.ceiling.value
        } : undefined,
        deductible: guarantee.deductible ? {
          ...guarantee.deductible,
          value: currentGuaranteeValues.deductible
            ? currentGuaranteeValues.deductible.value
            : guarantee.deductible.value
        } : undefined,
      };
    });

    dispatch(updateContractFunnelGuarantees({
      contractFunnelId: contractFunnel.id,
      contractIdentifier: contractFunnel.contractIdentifier,
      newGuarantees,
    }));

    closePanel();
  }, [activeGuarantees, getGuaranteeValue, dispatch, contractFunnel.id, contractFunnel.contractIdentifier, closePanel]);

  // Configurer les actions du panel
  useEffect(() => {
    setPanelActions({
      closeLabel: "COMMON.CANCEL",
      submitLabel: "Valider",
      onSubmit: handleSubmit
    });
  }, [handleSubmit, setPanelActions]);

  return {
    activeGuarantees,
    guaranteeValues,
    updateDeductible,
    updateCeiling,
    getGuaranteeValue,
    isActive,
    toggleGuarantee,
    handleSubmit
  };
};
